import type { JsonQuestion } from './types';

type StableQuestionAlias = {
  id: string;
  questionText: string;
  type: JsonQuestion['type'];
  targetMapping: {
    collection: string;
    field: string;
  };
};

/**
 * Template question IDs can survive inside metadata like optionVisibility even when
 * custom questionnaire snapshots contain copied questions with fresh `_id`s.
 */
const STABLE_QUESTION_ALIASES: StableQuestionAlias[] = [
  {
    id: '696f888e02616daa6cbaea46',
    questionText: 'Date of birth',
    type: 'date',
    targetMapping: { collection: 'clients', field: 'dateOfBirth' },
  },
  {
    id: '696f888e02616daa6cbaea47',
    questionText: 'What was your assigned sex at birth?',
    type: 'select',
    targetMapping: { collection: 'client_demographics', field: 'sexAtBirth' },
  },
  {
    id: '696f888e02616daa6cbaea48',
    questionText: 'Gender identity',
    type: 'select',
    targetMapping: { collection: 'clients', field: 'gender' },
  },
  {
    id: '696f888e02616daa6cbaea58',
    questionText: 'What is your typical physical activity level?',
    type: 'select',
    targetMapping: { collection: 'client_measurements', field: 'activityLevel' },
  },
];

export function getQuestionKey(question: JsonQuestion): string {
  return question.externalId || question._id;
}

function normalizeQuestionText(value: string): string {
  return value.trim().replace(/\s+/g, ' ').toLowerCase();
}

function questionMatchesStableAlias(question: JsonQuestion, alias: StableQuestionAlias): boolean {
  if (question.type !== alias.type) return false;

  const target = question.targetMapping;
  if (
    target?.collection === alias.targetMapping.collection &&
    target?.field === alias.targetMapping.field
  ) {
    return true;
  }

  return normalizeQuestionText(question.questionText) === normalizeQuestionText(alias.questionText);
}

/**
 * Maps API question ids (`_id`, `externalId`) to the storage key used in `answers` (see getQuestionKey).
 * Conditional fields like optionVisibility.ageQuestionId reference `_id`; answers are keyed by externalId when set.
 */
export function buildQuestionIdToStorageKeyMap(questions: JsonQuestion[] | undefined): Map<string, string> {
  const map = new Map<string, string>();
  const list = questions ?? [];
  for (const q of list) {
    const key = getQuestionKey(q);
    map.set(q._id, key);
    const ext = q.externalId?.trim();
    if (ext) map.set(ext, key);
  }

  for (const alias of STABLE_QUESTION_ALIASES) {
    const match = list.find((q) => questionMatchesStableAlias(q, alias));
    if (match) map.set(alias.id, getQuestionKey(match));
  }

  return map;
}

export function mergeQuestionIdToStorageKeyMaps(
  into: Map<string, string>,
  questions: JsonQuestion[] | undefined
): void {
  const sub = buildQuestionIdToStorageKeyMap(questions);
  for (const [k, v] of sub) into.set(k, v);
}

export function resolveStorageKeyForQuestionId(map: Map<string, string>, questionId: string): string {
  return map.get(questionId) ?? questionId;
}
